import { getPositionBadgeStyles, getPositionText } from '@/shows/utils/formatters';
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Home as HomeIcon,
  Event as EventIcon,
  DeleteSweep as DeleteSweepIcon,
  Reply as ReplyIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useAppContext } from "../../context";
import type { HoldList as HoldListType } from "api-client/models";
import { ChallengeResponseDialog } from "./ChallengeResponseDialog";
import { apiDateToDateOnly, formatDateForDisplay } from "@/utils/dateUtils";

import { MobileHoldCard } from '@/shows/components/ShowDetails/holds/MobileHoldCard';

// Interface for grouped show data
interface GroupedShow {
  showId: string;
  showTitle: string;
  holds: HoldListType[];
  startDate: string;
  endDate: string;
  dateCount: number;
  statuses: string[];
  promoterTeamName: string;
}

/**
 * HoldList - Display holds grouped by parent Show
 *
 * Features:
 * - Groups multi-day holds by parent Show
 * - Shows date range for multi-day holds
 * - Displays hold positions and statuses
 * - Search by show title
 * - Filter by status
 */
const HoldList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { apiClients } = useAppContext();

  const [holds, setHolds] = useState<HoldListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedHolds, setSelectedHolds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [selectedChallengedHold, setSelectedChallengedHold] =
    useState<HoldListType | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchHolds();
  }, []);

  const fetchHolds = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all holds via API (paginated response)
      const response = await apiClients.holds.holdsList();
      const holdsData = response.results || [];
      setHolds(holdsData);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load holds. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const allHoldIds = filteredShows.flatMap((show) =>
      show.holds.map((h) => h.id),
    );
    if (selectedHolds.size === allHoldIds.length) {
      setSelectedHolds(new Set());
    } else {
      setSelectedHolds(new Set(allHoldIds));
    }
  };

  const handleSelectShow = (holds: HoldListType[]) => {
    const holdIds = holds.map((h) => h.id);
    const allSelected = holdIds.every((id) => selectedHolds.has(id));
    const newSelected = new Set(selectedHolds);

    if (allSelected) {
      holdIds.forEach((id) => newSelected.delete(id));
    } else {
      holdIds.forEach((id) => newSelected.add(id));
    }
    setSelectedHolds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedHolds.size === 0) return;

    if (
      !confirm(
        `Are you sure you want to delete ${selectedHolds.size} hold${selectedHolds.size !== 1 ? "s" : ""}?`,
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      // Delete all selected holds
      await Promise.all(
        Array.from(selectedHolds).map((holdId) =>
          apiClients.holds.holdsDestroy({ id: holdId }),
        ),
      );

      setError(null);
      setSelectedHolds(new Set());
      await fetchHolds();
    } catch {
      setError("Failed to delete some holds. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChallengeDialog = (hold: HoldListType) => {
    setSelectedChallengedHold(hold);
    setChallengeDialogOpen(true);
  };

  const handleCloseChallengeDialog = () => {
    setChallengeDialogOpen(false);
    setSelectedChallengedHold(null);
  };

  const handleConfirmChallengedHold = async (holdId: string) => {
    try {
      await apiClients.holds.holdsRespondToChallenge({
        id: holdId,
        holdsRespondToChallengeRequest: {
          response: "confirm",
        },
      });
      setInfoMessage("Hold confirmed successfully! Challenge rejected.");
      await fetchHolds();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm hold");
      throw err;
    }
  };

  const handleReleaseChallengedHold = async (holdId: string) => {
    try {
      await apiClients.holds.holdsRespondToChallenge({
        id: holdId,
        holdsRespondToChallengeRequest: {
          response: "release",
        },
      });
      setInfoMessage("Hold released. Challenge cascade continues.");
      await fetchHolds();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to release hold");
      throw err;
    }
  };

  // Group holds by parent show
  const groupHoldsByShow = (holdsData: HoldListType[]): GroupedShow[] => {
    const showMap = new Map<string, GroupedShow>();

    holdsData.forEach((hold) => {
      // Use show ID if available, otherwise group by show title
      const groupKey = hold.show || hold.showTitle || "untitled";

      if (!showMap.has(groupKey)) {
        const holdDate = apiDateToDateOnly(hold.holdDate);
        showMap.set(groupKey, {
          showId: hold.show || "",
          showTitle: hold.showTitle || "Untitled Hold",
          holds: [],
          startDate: holdDate,
          endDate: holdDate,
          dateCount: 0,
          statuses: [],
          promoterTeamName: hold.promoterTeamName || "",
        });
      }

      const group = showMap.get(groupKey)!;
      group.holds.push(hold);
      const holdDate = apiDateToDateOnly(hold.holdDate);

      // Update date range
      if (holdDate < group.startDate) {
        group.startDate = holdDate;
      }
      if (holdDate > group.endDate) {
        group.endDate = holdDate;
      }

      // Collect unique statuses
      if (hold.status && !group.statuses.includes(hold.status)) {
        group.statuses.push(hold.status);
      }
    });

    // Calculate date count and sort holds by date
    const grouped = Array.from(showMap.values()).map((group) => ({
      ...group,
      dateCount: group.holds.length,
      holds: group.holds.sort(
        (a, b) =>
          apiDateToDateOnly(a.holdDate).localeCompare(apiDateToDateOnly(b.holdDate)),
      ),
    }));

    // Sort by start date (most recent first)
    return grouped.sort(
      (a, b) =>
        b.startDate.localeCompare(a.startDate),
    );
  };

  const getStatusColor = (
    status: string,
  ): "default" | "primary" | "warning" | "success" | "error" => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "primary";
      case "challenged":
        return "warning";
      case "confirmed":
        return "success";
      case "released":
        return "default";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  // Group holds by show and filter
  const groupedShows = groupHoldsByShow(holds);
  const filteredShows = groupedShows.filter((show) => {
    const matchesSearch = show.showTitle
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || show.statuses.includes(statusFilter);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          underline="hover"
          color="inherit"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
          Holds
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <Typography variant="h4" component="h1">
          Holds & Challenges
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/bookings/request")}>Request a host venue</Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/bookings/new")}
        >
          Create Hold
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Info Message Alert */}
      {infoMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setInfoMessage(null)}
        >
          {infoMessage}
        </Alert>
      )}

      {/* Challenge Warning Banner */}
      {holds.some((h) => h.status === "challenged") && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="body2" fontWeight="bold">
            ⚠️ You have {holds.filter((h) => h.status === "challenged").length}{" "}
            challenged hold(s) requiring response!
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Please respond within 24 hours or they will be automatically
            released.
          </Typography>
        </Alert>
      )}

      {/* Search and Filter */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search by title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Enter show title..."
          sx={{ flex: 1 }}
        />
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="challenged">Challenged</MenuItem>
          <MenuItem value="confirmed">Confirmed</MenuItem>
          <MenuItem value="released">Released</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          startIcon={<CalendarIcon />}
          onClick={() => navigate("/shows/calendar")}
        >
          Calendar View
        </Button>
      </Stack>

      {/* Bulk Actions Bar */}
      {selectedHolds.size > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3, flexWrap: 'wrap', '& .MuiAlert-action': { ml: 0, pl: 0 } }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="inherit"
                onClick={() => setSelectedHolds(new Set())}
              >
                Clear
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={<DeleteSweepIcon />}
                onClick={handleBulkDelete}
                disabled={isDeleting}
              >
                Delete {selectedHolds.size}
              </Button>
            </Stack>
          }
        >
          {selectedHolds.size} hold{selectedHolds.size !== 1 ? "s" : ""}{" "}
          selected
        </Alert>
      )}

      {/* Select All Checkbox */}
      {filteredShows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Checkbox
            checked={
              selectedHolds.size > 0 &&
              selectedHolds.size ===
                filteredShows.flatMap((s) => s.holds).length
            }
            indeterminate={
              selectedHolds.size > 0 &&
              selectedHolds.size < filteredShows.flatMap((s) => s.holds).length
            }
            onChange={handleSelectAll}
            slotProps={{ input: { 'aria-label': 'Select all holds' } }}
          />
          <Typography component="span" variant="body2" color="text.secondary">
            Select all {filteredShows.flatMap((s) => s.holds).length} hold
            {filteredShows.flatMap((s) => s.holds).length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}

      {/* Holds Table */}
      {filteredShows.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {searchTerm || statusFilter !== "all"
                ? "No holds match your search criteria."
                : "No holds found. Create your first hold to get started."}
            </Typography>
          </Box>
        </Card>
      ) : isMobile ? (
        <Stack spacing={2}>
          {filteredShows.map((show) => <Stack key={show.showId || show.showTitle} spacing={1}>
            <Button variant="outlined" onClick={() => navigate(`/bookings/${show.holds[0].id}`)}>
              {show.showTitle}
            </Button>
            <FormControlLabel sx={{ m: 0 }} label={`Select ${show.dateCount} holds for this show`} control={
              <Checkbox checked={show.holds.every((hold) => selectedHolds.has(hold.id))}
                indeterminate={show.holds.some((hold) => selectedHolds.has(hold.id)) && !show.holds.every((hold) => selectedHolds.has(hold.id))}
                onChange={() => handleSelectShow(show.holds)} />
            } />
            {show.holds.map((hold) => <MobileHoldCard key={hold.id} hold={hold} onRespond={handleOpenChallengeDialog}
              batch={{ selected: selectedHolds.has(hold.id), disabled: isDeleting, onSelect: () => handleSelectShow([hold]) }} />)}
          </Stack>)}
        </Stack>
      ) : (
        <TableContainer component={Paper} role="region" tabIndex={0} aria-label="Bookings by show"
          aria-busy={loading || isDeleting}>
          <Table aria-label="Show bookings" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedHolds.size > 0 &&
                      selectedHolds.size <
                        filteredShows.flatMap((s) => s.holds).length
                    }
                    checked={
                      selectedHolds.size > 0 &&
                      selectedHolds.size ===
                        filteredShows.flatMap((s) => s.holds).length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <strong>Show Title</strong>
                </TableCell>
                <TableCell>
                  <strong>Date(s)</strong>
                </TableCell>
                <TableCell>
                  <strong>Position</strong>
                </TableCell>
                <TableCell>
                  <strong>Stage</strong>
                </TableCell>
                <TableCell>
                  <strong>Venue</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Challenge</strong>
                </TableCell>
                <TableCell>
                  <strong>Promoter</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShows.map((show) => {
                const isMultiDay = show.dateCount > 1;
                const dateRange = isMultiDay
                  ? `${formatDateForDisplay(show.startDate, { month: "numeric", day: "numeric", year: "numeric" })} - ${formatDateForDisplay(show.endDate, { month: "numeric", day: "numeric", year: "numeric" })}`
                  : formatDateForDisplay(show.startDate, { month: "numeric", day: "numeric", year: "numeric" });

                // Get min/max positions from holds
                const positions = show.holds.map((h) => h.holdPosition || 0);
                const minPosition = Math.min(...positions);
                const maxPosition = Math.max(...positions);
                const positionDisplay =
                  minPosition === maxPosition
                    ? getPositionText(minPosition)
                    : `${getPositionText(minPosition)}-${getPositionText(maxPosition)}`;

                const showHoldIds = show.holds.map((h) => h.id);
                const allShowHoldsSelected = showHoldIds.every((id) =>
                  selectedHolds.has(id),
                );
                const someShowHoldsSelected = showHoldIds.some((id) =>
                  selectedHolds.has(id),
                );

                // Challenge information
                const challengedHolds = show.holds.filter(
                  (h) => h.status === "challenged",
                );
                const hasChallengedHolds = challengedHolds.length > 0;
                const firstChallengedHold = challengedHolds[0];

                return (
                  <TableRow
                    key={show.showId || show.showTitle}
                    hover
                    sx={{
                      bgcolor: hasChallengedHolds ? "warning.light" : "inherit",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allShowHoldsSelected}
                        indeterminate={
                          someShowHoldsSelected && !allShowHoldsSelected
                        }
                        onChange={() => handleSelectShow(show.holds)}
                      />
                    </TableCell>
                    <TableCell>
                      <MuiLink component={Link} to={`/bookings/${show.holds[0].id}`} variant="body2" fontWeight="medium">
                        {show.showTitle}
                      </MuiLink>
                      {isMultiDay && (
                        <Typography variant="caption" color="text.secondary">
                          {show.dateCount} days
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dateRange}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={positionDisplay}
                        size="small"
                        sx={{
                          ...getPositionBadgeStyles(minPosition),
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {show.holds[0]?.stageName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {show.holds[0]?.stageVenueName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {show.statuses.map((status) => (
                          <Chip
                            key={status}
                            label={status}
                            color={getStatusColor(status)}
                            size="small"
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {hasChallengedHolds ? (
                        <Stack direction="column" spacing={0.5}>
                          <Chip
                            label={`${challengedHolds.length} challenged`}
                            color="warning"
                            size="small"
                            icon={<WarningIcon />}
                          />
                          {firstChallengedHold && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              By:{" "}
                              {firstChallengedHold.challengerTeamName ||
                                "Unknown"}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {show.promoterTeamName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {hasChallengedHolds ? (
                        <Tooltip title="Respond to challenge">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() =>
                              handleOpenChallengeDialog(firstChallengedHold)
                            }
                          >
                            <ReplyIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Challenge Response Dialog */}
      {selectedChallengedHold && (
        <ChallengeResponseDialog
          open={challengeDialogOpen}
          onClose={handleCloseChallengeDialog}
          hold={selectedChallengedHold}
          onConfirm={handleConfirmChallengedHold}
          onRelease={handleReleaseChallengedHold}
        />
      )}
    </Box>
  );
};

export default HoldList;
